import styled from '@emotion/native';
import { useTheme } from '@emotion/react';
import { FONT_FAMILY, PADDING } from 'design-system/theme';
import type React from 'react';
import { useCallback, useRef, useState } from 'react';
import type { KeyboardType } from 'react-native';

import { Wrapper } from '../internal';

export interface TextBoxProps {
  label?: string;
  value?: string;
  placeholder?: string;
  errors?: string;
  isDisabled?: boolean;
  multiline?: boolean;
  numberOfLines?: number;
  onChange: (value: string) => void;
  keyboardType?: KeyboardType;
  onSubmitEditing?: () => void;
  autoResizeTextBox?: boolean;
}

const Input = styled.TextInput(({ theme }) => ({
  paddingVertical: PADDING['2xs'],
  paddingHorizontal: PADDING.xs,
  color: theme.color['text-default'],
  width: '100%',
  fontFamily: FONT_FAMILY.regular,
  fontSize: 14,
}));

export const TextBox: React.FC<TextBoxProps> = ({
  label = '',
  value = '',
  placeholder,
  errors,
  isDisabled,
  multiline,
  numberOfLines,
  keyboardType = 'default',
  onChange,
  onSubmitEditing,
  autoResizeTextBox = false,
}) => {
  const theme = useTheme();
  const [focused, setFocus] = useState(false);
  const [line, setLine] = useState<number>(1);
  const ref = useRef<any>(null);

  const handleFocus = useCallback(() => setFocus(true), [setFocus]);
  const handleBlur = useCallback(() => setFocus(false), [setFocus]);

  const editable = !isDisabled;

  const defaultNumberOfLines = multiline ? 5 : 1;
  const hasNumberOfLines = numberOfLines ? numberOfLines : defaultNumberOfLines;
  const resolvedNumberOfLines = autoResizeTextBox ? line : hasNumberOfLines;

  const lineSize = (size: number) => {
    const previousLineHeight = ref.current?.offsetHeight;

    if (autoResizeTextBox) {
      if (!previousLineHeight) {
        setLine(1);
        return;
      }

      if (size > previousLineHeight) {
        if (line < 5) {
          setLine(line + 1);
        }
      }

      if (size === previousLineHeight) {
        setLine(line - 1);
      }
    }
  };

  return (
    <Wrapper label={label} error={errors} focused={focused}>
      <Input
        ref={ref}
        keyboardType={keyboardType}
        onFocus={handleFocus}
        onSubmitEditing={onSubmitEditing}
        onBlur={handleBlur}
        value={value}
        placeholder={placeholder}
        onChangeText={onChange}
        placeholderTextColor={theme.color['text-disabled']}
        editable={editable}
        multiline={multiline}
        numberOfLines={resolvedNumberOfLines}
        onContentSizeChange={(e) => lineSize(e.nativeEvent.contentSize.height)}
      />
    </Wrapper>
  );
};
